import React, {useEffect} from "react";
import {Pressable, ScrollView} from "react-native";
import * as yup from "yup";
import {useAnalytics} from "@unibuddy/tracking";
import type {FormikHelpers, FormikState} from "formik";
import {
    Stack,
    Text,
    View,
    TextInput,
    LoadingButton,
    FormGroup,
    SubmitButton,
    Heading,
    Form,
    ErrorMessage,
    Inline,
    usePatronTheme,
} from "@unibuddy/patron";
import {useErrorReporting} from "@unibuddy/error-reporting";
import {useFormikContext} from "formik";
import {boldFontStyles} from "ubcommunity-shared/src/Styles/fontStyles";
import useAuth from "ubcommunity-shared/src/Auth/hooks/useAuth";
import {TrackEvents} from "ubcommunity-shared/src/constants";
import {MutationErrorHandler} from "ubcommunity-shared/src/General/Errors/MutationErrorHandler";
import {
    useRequestCommunityVerificationCodeMutation,
    useVerifyCommunityEmailMutation,
} from "../../types";
import {useLogout} from "../hooks/useLogout";

const AutoSubmit = (): React.ReactNode => {
    const {values, submitForm} = useFormikContext<{
        code: string;
    }>();

    useEffect(() => {
        if (values.code.length === 6) {
            submitForm();
        }
    }, [values.code, submitForm]);
    return null;
};

type Props = {
    onSuccess?: () => void;
};

export const VerifyEmailForm = ({onSuccess}: Props) => {
    const {authState, setAuth} = useAuth();
    const {trackEvent} = useAnalytics();
    const {logout} = useLogout();
    const theme = usePatronTheme();
    const [verify] = useVerifyCommunityEmailMutation();
    const [request, {loading}] = useRequestCommunityVerificationCodeMutation();
    const {reportError} = useErrorReporting();

    const {
        me: {
            anyUser: {email},
        },
    } = authState;

    const handleSubmit = async (
        {code}: {code: string},
        {
            setSubmitting,
            setFieldError,
        }: Pick<FormikHelpers<{code: string}>, "setSubmitting" | "setFieldError">,
    ) => {
        try {
            const {data} = await verify({variables: {code}});
            if (data.verifyAccount.success) {
                trackEvent(TrackEvents.PROSPECT_ACCOUNT_VERIFIED);
                setAuth({
                    ...authState,
                    me: {
                        ...authState?.me,
                        anyUser: {
                            ...authState?.me?.anyUser,
                            verified: true,
                        },
                    },
                });
                onSuccess && onSuccess();
            } else {
                setFieldError("serverError", data.verifyAccount.error);
            }
        } catch (error) {
            setFieldError("serverError", error.message);
            reportError(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequest = async () => {
        try {
            await request();
        } catch (error) {
            reportError(error);
        }
    };

    return (
        <ScrollView>
            <Form
                onSubmit={handleSubmit}
                initialValues={{code: "", serverError: ""}}
                validationSchema={{code: yup.string().required()}}
            >
                {({
                    errors,
                }: Pick<FormikState<{code: string; serverError?: "string"}>, "errors">) => (
                    <View flex="1" padding={["medium", "medium", "large"]} paddingTop="large">
                        <Stack space="large">
                            <Stack space="medium" align="center">
                                <Heading level="1" size="xxsmall" weight="700" align="center">
                                    Verify your account
                                </Heading>
                                <Text size="small">Check your email and enter the code below</Text>
                            </Stack>
                            <Text size="medium" weight="700" align="center">
                                {email}
                            </Text>
                            <Stack space="medium">
                                <View>
                                    <FormGroup label="Verification code">
                                        <TextInput name="code" maxLength={6} required />
                                    </FormGroup>
                                    <ErrorMessage
                                        component={
                                            <View paddingTop="medium">
                                                <MutationErrorHandler
                                                    error={errors.serverError}
                                                    message={errors.serverError}
                                                    meta={{
                                                        component: "VerifyEmailForm",
                                                        mutation: "useVerifyCommunityEmailMutation",
                                                    }}
                                                />
                                            </View>
                                        }
                                        name="serverError"
                                    />
                                </View>
                                <Stack space="small">
                                    <SubmitButton block color="primary">
                                        <Text size="large" color="white">
                                            Confirm code
                                        </Text>
                                    </SubmitButton>
                                    <LoadingButton
                                        loading={loading}
                                        type="button"
                                        ghost
                                        color="primary"
                                        block
                                        onClick={handleRequest}
                                    >
                                        <Text color="textLinkColor" size="large">
                                            Request new code
                                        </Text>
                                    </LoadingButton>
                                </Stack>
                                <View width="full">
                                    <Inline align="center" verticalAlign="center" space="xxsmall">
                                        <Text nowrap align="center" size="small">
                                            Not you?
                                        </Text>
                                        <Pressable onPress={logout}>
                                            <Text
                                                size="small"
                                                color={theme?.colors?.textLinkColor}
                                                // @ts-ignore
                                                style={{
                                                    ...boldFontStyles,
                                                    textDecorationLine: "underline",
                                                    textDecorationColor:
                                                        theme?.colors?.textLinkColor,
                                                }}
                                            >
                                                Change email
                                            </Text>
                                        </Pressable>
                                    </Inline>
                                </View>
                                <AutoSubmit />
                            </Stack>
                        </Stack>
                    </View>
                )}
            </Form>
        </ScrollView>
    );
};
